import { ChartPoint, ChartSeries } from '../report-aggregation';

/**
 * Reading a ranking grid, shared by every module that needs one.
 *
 * Extracted from the ranked driver when the ownership matrix wanted the same "Top 3 Priority"
 * figure in a column. The two must never disagree about it -- the same deck puts them on facing
 * pages -- and the only way to guarantee that is one implementation, which is the rule the data
 * table was built on for the same reason.
 *
 * What stays with each block is what each block does with these numbers: the driver sorts them into
 * tiers and draws bars, the ownership matrix shades a column.
 */

/** Rank positions counted as "top" when a block does not say. */
export const DEFAULT_TOP_RANKS = 3;

/** One attribute of a ranking grid. */
export interface RankRow {
  /** The question's own label, which is how another module joins to it. */
  attribute: string;
  base: number;
  /** Share ranking it first, and the share ranking it anywhere in the top few. */
  rankOne: number;
  top: number;
}

export interface RankReading {
  rows: RankRow[];
  /** Why a row could not be read, or how the settings were clamped. Never silent. */
  problems: string[];
  /** The top count actually used, which may be lower than the one asked for. */
  topRanks: number;
  /** Rank positions the grid asked, 0 where none of the questions is a ranking. */
  rankCount: number;
}

export interface RankSettings {
  topRanks?: number;
  rankOneEnd?: 'first' | 'last';
}

/**
 * The first-place and top-N shares of every row of a ranking grid.
 *
 * Rows come back in the order given; ordering is the caller's business. A row that cannot be read as
 * a ranking is left out and the reason is in `problems`.
 */
export function readRanks(series: readonly ChartSeries[], settings: RankSettings): RankReading {
  const problems: string[] = [];

  // How many rank positions the grid has, taken across every row rather than from one of them.
  // aggregate() drops an option nobody chose, so a single row can be short of a rank -- and a rank
  // nobody used anywhere in the grid does not exist for this chart's purposes either.
  const rankCount = countRanks(series);
  if (!rankCount) {
    // Diagnosed row by row rather than as one blanket note: a grid of multi-punch questions has no
    // ranks in it *and* plenty of data, and "no data" would send someone to look at the wrong thing
    // entirely.
    return {
      rows: [],
      problems: series.map(s => problemWith(s) ?? noRanksNote(s.name)),
      topRanks: 0,
      rankCount: 0,
    };
  }

  // Top N of N ranks is everyone, which says nothing whichever way it is drawn.
  const asked = Math.max(1, Math.trunc(settings.topRanks ?? DEFAULT_TOP_RANKS));
  const topRanks = Math.min(asked, Math.max(1, rankCount - 1));
  if (topRanks !== asked) {
    problems.push(`Top ${asked} of ${rankCount} ranks would be everyone, `
      + `so this shows the top ${topRanks}.`);
  }

  // Which end is rank #1 is a setting rather than an inference: a survey writes a rank scale either
  // way round and the datamap does not record which, so read the wrong way this does not degrade,
  // it inverts -- the attributes nobody wants are presented as the drivers.
  const flipped = settings.rankOneEnd === 'last';
  const indexOfRank = (rank: number) => flipped ? rankCount - rank : rank - 1;
  const topIndexes = new Set<number>();
  for (let rank = 1; rank <= topRanks; rank++) topIndexes.add(indexOfRank(rank));

  const rows: RankRow[] = [];
  for (const s of series) {
    const problem = problemWith(s);
    if (problem) {
      problems.push(problem);
      continue;
    }
    rows.push(rowFor(s, indexOfRank(1), topIndexes));
  }

  return { rows, problems, topRanks, rankCount };
}

/** The number of rank positions asked, as the widest row saw them. */
function countRanks(series: readonly ChartSeries[]): number {
  const indexes = series
    .filter(s => s.known && !s.multiResponse)
    .flatMap(s => s.points.map(point => point.optionIndex))
    .filter(index => index >= 0);
  return indexes.length ? Math.max(...indexes) + 1 : 0;
}

/** Why this row cannot be read as a ranking, or null if it can. */
export function problemWith(s: ChartSeries): string | null {
  if (!s.known) return `No data for ${s.name}.`;
  // A multi-punch question has no ranks in it at all: a respondent can pick several answers, so no
  // answer is a position. Caught by what the question *is* rather than by its arithmetic, which is
  // the more honest test of the two.
  if (s.multiResponse) return noRanksNote(s.name, 'accepts more than one answer');
  if (s.base === 0) return `Nobody ranked ${s.name}.`;
  if (!s.points.some(point => point.optionIndex >= 0)) return noRanksNote(s.name);
  return null;
}

function noRanksNote(name: string, because = 'has no answers to read as rank positions'): string {
  return `${name} ${because}, so its answers are not ranks. `
    + 'Each tiered ranking row needs a ranking question.';
}

function rowFor(s: ChartSeries, rankOneIndex: number, topIndexes: Set<number>): RankRow {
  // Selected by option position, never by label: Decipher titles only the ends of a rank scale --
  // "Trust the most", five blanks, "Trust the least" -- so there is nothing in between to match on.
  // By position rather than by array index for the same reason the rank count is taken across rows:
  // aggregate() drops an option nobody chose, and slicing would then take rank 3 for rank 2.
  const at = (index: number): ChartPoint | undefined =>
    s.points.find(point => point.optionIndex === index);

  return {
    attribute: s.name,
    base: s.base,
    rankOne: at(rankOneIndex)?.percent ?? 0,
    top: s.points
      .filter(point => topIndexes.has(point.optionIndex))
      .reduce((sum, point) => sum + point.percent, 0),
  };
}
